/**
 * SchemaDraft — UI-friendly mutable schema shape used by the visual builder.
 *
 * Design notes: docs/house-check-request/visual-builder-design.md
 *
 * Two layers:
 *   - SchemaDraft / SectionDraft / FieldDraft   — mutable, with UI-only fields (`uiId`, `keyLocked`).
 *   - draftTo* converters strip UI state and return the on-the-wire shapes.
 */

import type {
  AttachableRecordType,
  CreateFormSchemaCommandData,
  FormFieldData,
  FormFieldOptionData,
  FormFieldShowIfData,
  FormFieldType,
  FormSchemaData,
  FormSectionData,
} from '@/services/api-types';

let uiIdCounter = 0;
export function newUiId(): string {
  uiIdCounter += 1;
  return `ui_${uiIdCounter}`;
}

export interface FieldDraft {
  uiId: string;
  key: string;
  keyLocked: boolean;
  /**
   * True when this field was loaded from a saved server schema (vs. added during the
   * current edit session). Used by the editor to lock the `searchable` checkbox: changes
   * to searchable on existing fields must go through the dedicated toggle endpoint
   * (PUT /admin/form/schema/{id}/field/{key}/searchable), not through save.
   *
   * False / undefined for fields added in this session — searchable is freely editable
   * because the new field has no instance data to reindex.
   */
  isPersisted?: boolean;
  label: string;
  type: FormFieldType;
  required?: boolean;
  searchable?: boolean;
  summary?: boolean;
  /**
   * If true, surface this field on the live tour stop detail page's extra tab
   * for this schema. Independent of `summary`, which
   * controls the post-completion Records Search summary panel.
   */
  tab?: boolean;
  showIf?: FormFieldShowIfData;
  options?: FormFieldOptionData[];
  columns?: FieldDraft[];
  formula?: string;
  /**
   * Optional sub-section heading rendered above this field, with a horizontal rule.
   * Use to visually group several fields under a divider inside a single section
   * without splitting the schema into multiple sections.
   */
  subheading?: string;
  /**
   * If true, render this field on the same row as the preceding visible field
   * instead of stacking. Used for compact "question + follow-up" patterns
   * (e.g. "Lights on timer? Y/N + Locations [_____]").
   */
  inlineWithPrevious?: boolean;
  /**
   * For TABLE fields only. When set, the table renders these as locked rows
   * (no add/remove) with the row.label shown in a prepended first column.
   * Other column cells remain user-editable.
   */
  predefinedRows?: { id: string; label: string }[];
  /** Force this field to span the full row in the section grid. */
  wide?: boolean;
  /** Display formatter (computed/number fields). e.g. 'currency'. */
  format?: string;
  /** Row layout: 'horizontal' for label-left / value-right (billing/summary). */
  layout?: 'horizontal';
}

export interface SectionDraft {
  uiId: string;
  id: string;
  idLocked: boolean;
  title: string;
  fields: FieldDraft[];
  /**
   * If true, render this section in the right-side column on wider screens
   * (≥ ~900px). Useful for narrow auxiliary cards like billing or controls.
   * On narrow screens all sections collapse to a single column.
   */
  sidebar?: boolean;
}

export interface SchemaDraft {
  id: number | null;
  name: string;
  attachTo: AttachableRecordType | string;
  isActive: boolean;
  sections: SectionDraft[];
}

export function isOptionType(t: FormFieldType): boolean {
  return t === 'radio' || t === 'select' || t === 'checkboxGroup';
}

export function isScalarType(t: FormFieldType): boolean {
  // Permitted as a TABLE column type per §4.1 rule 9 (no nested table, no computed inside table).
  return (
    t === 'text' || t === 'textarea' || t === 'phone' || t === 'email' ||
    t === 'date' || t === 'number' || t === 'radio' || t === 'select' ||
    t === 'checkboxGroup'
  );
}

/** Apply per-type defaults; drop properties incompatible with the new type. */
export function applyTypeDefaults(field: FieldDraft): FieldDraft {
  const next: FieldDraft = { ...field };
  if (!isOptionType(next.type)) delete next.options;
  if (next.type !== 'table') {
    delete next.columns;
    delete next.predefinedRows;
  }
  if (next.type !== 'computed') delete next.formula;
  if (isOptionType(next.type) && !next.options) next.options = [];
  if (next.type === 'table') {
    if (!next.columns) next.columns = [];
    delete next.searchable;
    delete next.summary;
  }
  if (next.type === 'computed' && next.formula == null) next.formula = '';
  return next;
}

export function newFieldDraft(type: FormFieldType = 'text'): FieldDraft {
  return applyTypeDefaults({
    uiId: newUiId(),
    key: '',
    keyLocked: false,
    label: '',
    type,
  });
}

export function newSectionDraft(): SectionDraft {
  return {
    uiId: newUiId(),
    id: '',
    idLocked: false,
    title: '',
    fields: [],
  };
}

export function blankSchemaDraft(): SchemaDraft {
  return {
    id: null,
    name: '',
    attachTo: 'CLIENT',
    isActive: true,
    sections: [],
  };
}

export function schemaToDraft(schema: FormSchemaData | null): SchemaDraft {
  if (!schema) return blankSchemaDraft();
  const sections = schema.currentVersion?.sections ?? [];
  return {
    id: schema.id,
    name: schema.name,
    attachTo: schema.attachTo,
    isActive: schema.isActive,
    sections: sections.map(sectionDataToDraft),
  };
}

function sectionDataToDraft(s: FormSectionData): SectionDraft {
  return {
    uiId: newUiId(),
    id: s.id,
    idLocked: typeof s.id === 'string' && s.id.length > 0,
    title: s.title,
    fields: (s.fields ?? []).map(fieldDataToDraft),
    sidebar: s.sidebar,
  };
}

function fieldDataToDraft(f: FormFieldData): FieldDraft {
  return {
    uiId: newUiId(),
    key: f.key,
    keyLocked: typeof f.key === 'string' && f.key.length > 0,
    isPersisted: true,  // came from server → searchable changes go through toggle endpoint
    label: f.label,
    type: f.type,
    required: f.required,
    searchable: f.searchable,
    summary: f.summary,
    tab: f.tab,
    showIf: f.showIf,
    options: f.options ? f.options.map((o) => ({ ...o })) : undefined,
    columns: f.columns ? f.columns.map(fieldDataToDraft) : undefined,
    formula: f.formula,
    subheading: f.subheading,
    inlineWithPrevious: f.inlineWithPrevious,
    predefinedRows: f.predefinedRows ? f.predefinedRows.map((r) => ({ ...r })) : undefined,
    wide: f.wide,
    format: typeof f.format === 'string' ? f.format : undefined,
    layout: f.layout === 'horizontal' ? 'horizontal' : undefined,
  };
}

/** Strip UI-only state and return the create/update command payload. */
export function draftToSchemaCommand(draft: SchemaDraft): CreateFormSchemaCommandData {
  return {
    name: draft.name,
    attachTo: draft.attachTo,
    sections: draft.sections.map(sectionDraftToData),
  };
}

function sectionDraftToData(s: SectionDraft): FormSectionData {
  const out: FormSectionData = {
    id: s.id,
    title: s.title,
    fields: s.fields.map(fieldDraftToData),
  };
  if (s.sidebar) out.sidebar = true;
  return out;
}

function fieldDraftToData(f: FieldDraft): FormFieldData {
  const out: FormFieldData = {
    key: f.key,
    label: f.label,
    type: f.type,
  };
  if (f.required) out.required = true;
  if (f.searchable) out.searchable = true;
  if (f.summary) out.summary = true;
  if (f.tab) out.tab = true;
  if (f.showIf) out.showIf = f.showIf;
  if (f.options && f.options.length > 0) out.options = f.options.map((o) => ({ ...o }));
  if (f.columns && f.columns.length > 0) out.columns = f.columns.map(fieldDraftToData);
  if (f.formula) out.formula = f.formula;
  // subheading is preserved even when empty, since "" means divider-only.
  if (f.subheading !== undefined) out.subheading = f.subheading;
  if (f.inlineWithPrevious) out.inlineWithPrevious = true;
  if (f.predefinedRows && f.predefinedRows.length > 0) {
    out.predefinedRows = f.predefinedRows.map((r) => ({ ...r }));
  }
  if (f.wide) out.wide = true;
  if (f.format) out.format = f.format;
  if (f.layout === 'horizontal') out.layout = 'horizontal';
  return out;
}

/** JSON view used by the JSON tab — what would be POSTed, pretty-printed. */
export function draftToJSON(draft: SchemaDraft): string {
  return JSON.stringify(draftToSchemaCommand(draft), null, 2);
}

/** Parse the JSON tab's textarea back into a draft. Throws on malformed JSON. */
export function jsonToDraft(json: string, existingId: number | null): SchemaDraft {
  const parsed = JSON.parse(json);
  if (!parsed || typeof parsed !== 'object') throw new Error('JSON must be an object.');
  const sections: SectionDraft[] = Array.isArray(parsed.sections)
    ? parsed.sections.map((s: FormSectionData) => sectionDataToDraft(s))
    : [];
  return {
    id: existingId,
    name: typeof parsed.name === 'string' ? parsed.name : '',
    attachTo: typeof parsed.attachTo === 'string' ? parsed.attachTo : 'CLIENT',
    isActive: true,
    sections,
  };
}

/**
 * Slug a label per FieldKeyGenerator (§2.4):
 *   - lowercase
 *   - replace runs of non-[a-zA-Z0-9_] with `_`
 *   - leading digit gets `f_` prefix
 *   - truncate to 128 chars
 *   - returns '' when no usable ASCII characters survive
 */
export function slugLabel(label: string): string {
  const trimmed = (label ?? '').trim();
  if (trimmed.length === 0) return '';
  let slug = trimmed.toLowerCase().replace(/[^a-z0-9_]+/g, '_').replace(/^_+|_+$/g, '');
  if (slug.length === 0) return '';
  if (/^[0-9]/.test(slug)) slug = `f_${slug}`;
  if (slug.length > 128) slug = slug.substring(0, 128);
  return slug;
}
